import { put, takeLatest } from 'redux-saga/effects';
import { GraphQL, gql, History } from 'core';
import Cookies from 'js-cookie';
import { selectState } from 'store';
import actionTypes, {
  LoginRequestActionCreatorType,
  LoginResponseType,
  ACCESS_TOKEN_COOKIE_NAME,
  REFRESH_TOKEN_COOKIE_NAME,
  actionQueueType,
  ResendEmailRequestActionCreatorType,
  ResendEmailResponseType
} from './types';
import selector from './selector';
import actions from './actions';
import routes from 'components/Router/routes';
import Mixpanel from 'mixPanel';
import InitializeIntercom from 'intercom';
import { getLocalValue } from 'localization';
import { logout as authLogout } from 'core/modules/auth.utils';
import { actions as userDetailAction } from 'userDetail';

export function* logout() {
  Mixpanel.track('models:log_out');
  yield authLogout();
}

export function* loginRequest({ email, password, redirectTo }: LoginRequestActionCreatorType) {
  try {
    yield put(actions.loginRequestPending());
    const {
      data: { tokenAuth }
    }: LoginResponseType = yield GraphQL.mutate({
      mutation: gql`
        mutation tokenAuthMutation($email: String!, $password: String!) {
          tokenAuth(email: $email, password: $password) {
            token
            refreshToken
          }
        }
      `,
      variables: {
        email,
        password
      },
      fetchPolicy: 'no-cache'
    });

    if (tokenAuth?.token && tokenAuth?.refreshToken) {
      const accessToken = tokenAuth.token;
      const refreshToken = tokenAuth.refreshToken;
      Cookies.set(ACCESS_TOKEN_COOKIE_NAME, accessToken);
      Cookies.set(REFRESH_TOKEN_COOKIE_NAME, refreshToken);
      yield put(actions.loginRequestSuccess(accessToken, refreshToken, email));

      yield put(userDetailAction.getUserDetails());

      Mixpanel.identify(email);
      Mixpanel.people.set({
        $email: email,
        $last_login: new Date()
      });
      if (process.env.REACT_APP_ENABLE_INTERCOM === 'true') {
        InitializeIntercom();
      }
      if (redirectTo) {
        History.push(redirectTo);
      } else {
        History.push(routes.MODELS);
      }

      const actionQueue: actionQueueType = yield selectState<actionQueueType>((state) =>
        selector.getActionQueue(state)
      );
      if (actionQueue) {
        yield put(actionQueue);
      }
    }
  } catch (error) {
    const acNotVerifiedError: string = yield selectState<string | undefined>((state) =>
      getLocalValue(state, 'account_not_verified')
    );

    const geniricError: string = yield selectState<string | undefined>((state) =>
      getLocalValue(state, 'error_msg')
    );

    const acNotRegisteredErr: string = yield selectState<string | undefined>((state) =>
      getLocalValue(state, 'email_not_registered')
    );

    const acNotRegistered: string = yield selectState<string | undefined>((state) =>
      getLocalValue(state, 'account_not_registered')
    );
    const resetPasswordExpired: string = yield selectState<string | undefined>((state) =>
      getLocalValue(state, 'reset_password_expired')
    );
    const errorMsg = (error as { message: string })?.message ?? geniricError;
    if (errorMsg === acNotVerifiedError) {
      yield put(actions.loginRequestErorr(errorMsg, true));
    } else if (errorMsg === acNotRegisteredErr) {
      const errorMsg = acNotRegistered ?? geniricError;
      yield put(actions.loginRequestErorr(errorMsg));
    } else if (errorMsg === resetPasswordExpired) {
      yield put(actions.loginRequestErorr(errorMsg, false, true));
    } else {
      yield put(actions.loginRequestErorr(errorMsg));
    }
  }
}

export function* resendEmailRequest({ email }: ResendEmailRequestActionCreatorType) {
  try {
    yield put(actions.resendEmailRequestPending());
    const {
      data: {
        resendVerificationEmail: { success, errors }
      }
    }: ResendEmailResponseType = yield GraphQL.mutate({
      mutation: gql`
        mutation resendVerificationEmailMutation($email: String!) {
          resendVerificationEmail(email: $email) {
            success
            errors
          }
        }
      `,
      variables: {
        email
      },
      fetchPolicy: 'no-cache'
    });

    if (success) {
      yield put(actions.resendEmailRequestSuccess());
      History.push(`${routes.REGISTER_THANKYOU_POPUP}?email=${email}.`);
    } else {
      const acNotRegisteredErr: string = yield selectState<string | undefined>((state) =>
        getLocalValue(state, 'email_not_registered')
      );

      const geniricError: string = yield selectState<string | undefined>((state) =>
        getLocalValue(state, 'error_msg')
      );

      const acNotRegistered: string = yield selectState<string | undefined>((state) =>
        getLocalValue(state, 'account_not_registered')
      );

      if (errors[0] === acNotRegisteredErr) {
        const errorMsg = acNotRegistered;
        yield put(actions.resendEmailRequestErorr(errorMsg));
      } else {
        const errorMsg = geniricError;
        yield put(actions.resendEmailRequestErorr(errorMsg));
      }
    }
  } catch (error) {
    const errorMsg = (error as { message: string })?.message;
    yield put(actions.resendEmailRequestErorr(errorMsg));
  }
}

export default function* watchIncrementAsync() {
  yield takeLatest(actionTypes.LOGIN_REQUEST, loginRequest);
  yield takeLatest(actionTypes.RESEND_EMAIL_REQUEST, resendEmailRequest);
  yield takeLatest(actionTypes.LOGOUT, logout);
}
